import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h.includes('ondigitalocean.app') || h.includes('services.knls.in')) {
      return `${window.location.origin}/api`;
    }
  }
  return 'http://localhost:3000/api';
};

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const builderNameOf = (c) => {
  const name = String(c.builderName || c.brandLabel || '').trim();
  if (name) return name;
  if (c.catalogSource === 'lms' || c.publisherSource === 'platform') return 'KNLS';
  return 'LMS Builder';
};

/** Student Course Referrals — grouped by LMS builder + search/sort. */
export default function StudentReferralsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState([]);
  const [mySales, setMySales] = useState({ items: [], summary: {} });
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('builder');

  const token = () =>
    localStorage.getItem('student_token') ||
    localStorage.getItem('studentToken') ||
    localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${getApiBaseUrl()}/students/me/referrals`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = res.data?.data || {};
      setAvailable(Array.isArray(data.available) ? data.available : []);
      setMySales(data.mySales || { items: [], summary: {} });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = available.filter((c) => {
      if (!q) return true;
      const hay = [c.courseName, c.courseCode, builderNameOf(c), c.brandLabel]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
    const n = (v) => Number(v) || 0;
    if (sort === 'name') {
      list = [...list].sort((a, b) =>
        String(a.courseName || '').localeCompare(String(b.courseName || '')),
      );
    } else if (sort === 'feeAsc') {
      list = [...list].sort((a, b) => n(a.onlineFee) - n(b.onlineFee));
    } else if (sort === 'feeDesc') {
      list = [...list].sort((a, b) => n(b.onlineFee) - n(a.onlineFee));
    } else if (sort === 'commission') {
      list = [...list].sort(
        (a, b) => n(b.estimatedCommissionRupees) - n(a.estimatedCommissionRupees),
      );
    } else {
      list = [...list].sort((a, b) => {
        const ba = builderNameOf(a).toLowerCase();
        const bb = builderNameOf(b).toLowerCase();
        if (ba === 'knls' && bb !== 'knls') return -1;
        if (bb === 'knls' && ba !== 'knls') return 1;
        const g = ba.localeCompare(bb);
        return g || String(a.courseName || '').localeCompare(String(b.courseName || ''));
      });
    }
    const map = new Map();
    list.forEach((c) => {
      const key = builderNameOf(c);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    const entries = [...map.entries()];
    entries.sort((a, b) => {
      if (a[0].toLowerCase() === 'knls') return -1;
      if (b[0].toLowerCase() === 'knls') return 1;
      return a[0].localeCompare(b[0]);
    });
    return entries;
  }, [available, query, sort]);

  const openShare = async (course) => {
    setBusy(true);
    try {
      let link = course;
      if (!course.shareUrl || !course.myToken) {
        const res = await axios.post(
          `${getApiBaseUrl()}/students/me/referrals/links`,
          {
            courseCode: course.courseCode,
            publisherBusinessId: course.publisherBusinessId,
            acceptTerms: true,
          },
          { headers: { Authorization: `Bearer ${token()}` } },
        );
        link = res.data?.data || course;
      }
      const text = link.whatsappText || link.shareUrl || '';
      window.open(
        `https://wa.me/?text=${encodeURIComponent(text)}`,
        '_blank',
        'noopener,noreferrer',
      );
      if (link.id || link.myLinkId) {
        try {
          await axios.post(
            `${getApiBaseUrl()}/students/me/referrals/links/${link.id || link.myLinkId}/shared`,
            {},
            { headers: { Authorization: `Bearer ${token()}` } },
          );
        } catch (_) {
          /* optional */
        }
      }
      await load();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const items = Array.isArray(mySales.items) ? mySales.items : [];
  const summary = mySales.summary || {};

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      <Button onClick={() => navigate('/student/dashboard')} sx={{ mb: 1 }}>
        ← Dashboard
      </Button>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Course Referrals
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Courses are grouped by LMS builder. App Admin catalog = <strong>KNLS</strong>. Other coaching
        centers appear under their builder name.
      </Alert>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Available" />
        <Tab label="My sales" />
      </Tabs>

      {tab === 0 && (
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              fullWidth
              label="Search course or builder"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort</InputLabel>
              <Select
                label="Sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <MenuItem value="builder">Builder · name</MenuItem>
                <MenuItem value="name">Course name A–Z</MenuItem>
                <MenuItem value="feeAsc">Fee: low → high</MenuItem>
                <MenuItem value="feeDesc">Fee: high → low</MenuItem>
                <MenuItem value="commission">Commission: high → low</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {!grouped.length ? (
            <Typography color="text.secondary">
              No courses match. Publish under Course Management or enable Referral % on Learn Zone courses.
            </Typography>
          ) : (
            grouped.map(([builder, courses]) => (
              <Box key={builder}>
                <Typography fontWeight={800} sx={{ mt: 1, mb: 1 }}>
                  {builder.toLowerCase() === 'knls'
                    ? 'KNLS (App Admin published)'
                    : builder}{' '}
                  <Chip size="small" label={courses.length} sx={{ ml: 0.5 }} />
                </Typography>
                <Stack spacing={1}>
                  {courses.map((c) => (
                    <Card key={c.courseCode} variant="outlined">
                      <CardContent>
                        <Typography fontWeight={800}>{c.courseName}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Fee {money(c.onlineFee)} · ~{c.ratePercent}% (~
                          {money(c.estimatedCommissionRupees)}) — paid by publisher, not Locsy
                        </Typography>
                        <Button variant="contained" disabled={busy} onClick={() => openShare(c)}>
                          WhatsApp referral link
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            ))
          )}
        </Stack>
      )}

      {tab === 1 && (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Chip label={`Owed ${money(summary.owedRupees)}`} />
            <Chip label={`Settled ${money(summary.settledRupees)}`} color="success" />
            <Chip label={`Total ${summary.totalCount || items.length}`} />
          </Stack>
          {!items.length ? (
            <Typography color="text.secondary">
              No sales yet. Share from Available; successful enrollments appear here.
            </Typography>
          ) : (
            items.map((r) => (
              <Card key={r.id} variant="outlined" sx={{ mb: 1 }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography fontWeight={700}>{r.courseName || r.courseCode}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {money(r.commissionRupees)} · {r.status} · {r.citizenPhone || ''}
                  </Typography>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
